import React, {useState, useRef} from 'react'
import styled from 'styled-components'
import Container from '@material-ui/core/Container'
import Header from 'Event/template/Cards/Dashboard/Header'
import Menu from 'Event/template/Cards/Menu'
import {User} from 'auth/user'
import Footer from 'Event/template/Cards/Dashboard/Footer'
import {Cards, useCardsTemplate} from 'Event/template/Cards'
import {rgba, safeColor} from 'lib/color'
import LanguageSelector from 'Event/LanguageSelector'
import {muiTheme} from 'lib/ui/theme'
import {ThemeProvider} from '@material-ui/core/styles'
import {withStyles} from '@material-ui/core/styles'
import MainNav from 'Event/template/Cards/Dashboard/MainNav'
import {useRandomBackground} from 'Event/template/Cards/Login/Page'
import {createMuiLightTheme, MuiThemeOptions} from 'lib/ui/theme'
import IconButton from 'lib/ui/IconButton'
import {Icon} from 'lib/fontawesome/Icon'
import Typography from '@material-ui/core/Typography'
import {useAttendeeVariables} from 'Event'
import Bar from 'Event/template/Cards/Dashboard/PageLinks'
import {BottomBox, Content} from 'Event/template/Cards/Page'
import CountDownTimers from 'Event/template/Cards/Dashboard/CountDownTimers'
import Hero from 'Event/template/Cards/Dashboard/Hero'
import {Font, useLoadFont} from 'lib/FontSelect'
import {BackgroundPosition} from 'lib/ui/BackgroundPositionSelector'
import {scrollIntoView} from 'seamless-scroll-polyfill'

export default function CardsPage(props: {
  user: User
  children: React.ReactElement | React.ReactElement[]
}) {
  const [menuVisible, setMenuVisible] = useState(false)
  const toggleMenu = () => setMenuVisible(!menuVisible)
  const template = useCardsTemplate()

  const {background} = template

  const dashboardBackground = useRandomBackground()

  const backgroundImage = dashboardBackground ? dashboardBackground.image : null
  const backgroundPosition = dashboardBackground
    ? dashboardBackground.position
    : null

  const backgroundRGBColor = rgba(background.color, background.opacity / 100)

  const color = template.textColor
  const linkUnderline = template.linkUnderline
  const linkColor = template.linkColor
  const font = template.font

  useLoadFont(font)

  const contentRef = useRef<HTMLDivElement>(null)

  return (
    <ThemeProvider theme={muiTheme}>
      <TopBox
        backgroundImage={backgroundImage}
        backgroundPosition={backgroundPosition}
        color={color}
        linkColor={linkColor}
        linkUnderline={linkUnderline}
        position={template.backgroundPosition}
        font={font}
        data-testid="page container"
      >
        <ColorOverlay color={backgroundRGBColor}>
          <Menu visible={menuVisible} toggle={toggleMenu} user={props.user} />
          <Header
            menuVisible={menuVisible}
            toggleMenu={toggleMenu}
            aria-label="header"
            isDashboardHeader={true}
          />
          <TopContainer>
            <Top>
              <CountDownTimers />
              <MainNav />
            </Top>
            <ScrollDownIndicator contentRef={contentRef} />
          </TopContainer>
        </ColorOverlay>
      </TopBox>
      <Content>
        <BottomBox ref={contentRef}>
          <Hero />
          <Bar />
          <Box
            color={color}
            linkColor={linkColor}
            linkUnderline={linkUnderline}
            font={font}
          >
            <BottomContainer>{props.children}</BottomContainer>
          </Box>
        </BottomBox>
      </Content>
      <LanguageSelector />
      <Footer />
    </ThemeProvider>
  )
}

function ScrollDownIndicator(props: {
  contentRef: React.RefObject<HTMLDivElement>
}) {
  const v = useAttendeeVariables()
  const {mainNav} = useCardsTemplate()

  const executeScroll = () => {
    if (!props.contentRef || !props.contentRef.current) {
      return
    }
    scrollIntoView(props.contentRef.current, {behavior: 'smooth'})
  }

  return (
    <StyledDownContainer color={mainNav.scrollDownArrowColor}>
      <AnimationIcon>
        <IconButton onClick={executeScroll}>
          <ArrowIcon
            iconClass={`far fa-angle-down`}
            color={mainNav.scrollDownArrowColor}
          />
        </IconButton>
      </AnimationIcon>
      <Typography>{v(mainNav.scrollDownText)}</Typography>
    </StyledDownContainer>
  )
}

export function useTheme() {
  const template = useCardsTemplate()

  const options: MuiThemeOptions = {
    secondaryColor: safeColor(template.accentColor),
  }

  return createMuiLightTheme(options)
}

const Box = styled.div<{
  color: string
  linkColor: string
  linkUnderline: boolean
  font: Font | null
}>`
  width: 100%;
  font-size: 17px;
  ${(props) => (props.font ? `font-family: ${props.font.family};` : '')}
  color: ${(props) => props.color};

  a {
    color: ${(props) => props.linkColor};
    text-decoration: none;

    &:hover {
      text-decoration: ${(props) =>
        props.linkUnderline ? 'underline' : 'none'};
    }
  }
`

export const TopBox = styled.div<{
  backgroundImage: string | null
  backgroundPosition: BackgroundPosition | null
  color: string
  linkColor: string
  linkUnderline: boolean
  position: Cards['backgroundPosition']
  font: Font | null
  fullHeight?: boolean
}>`
  height: ${(props) => (props.fullHeight ? '100vh' : 'auto')};
  font-size: 17px;
  ${(props) => (props.font ? `font-family: ${props.font.family};` : '')}
  color: ${(props) => props.color};
  ${(props) =>
    props.backgroundImage ? `background: url(${props.backgroundImage});` : ''}
  ${(props) =>
    props.position === 'bottom' &&
    `
      background-size: cover;
    `}
  ${(props) =>
    props.position === 'fixed' &&
    `
      background-attachment: fixed;
      background-size: cover;
    `}
  background-repeat: no-repeat;
  ${(props) =>
    props.backgroundPosition &&
    `background-position: ${props.backgroundPosition};`}

  a {
    color: ${(props) => props.linkColor};
    text-decoration: none;

    &:hover {
      text-decoration: ${(props) =>
        props.linkUnderline ? 'underline' : 'none'};
    }
  }
  -webkit-transition: all 300ms ease-in 200ms;
  -moz-transition: all 300ms ease-in 200ms;
  -o-transition: all 300ms ease-in 200ms;
  transition: all 300ms ease-in 200ms;
`

const ColorOverlay = styled.div<{
  color: string
}>`
  background-color: ${(props) => props.color};
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: auto;
  min-height: 100vh;
`

const TopContainer = withStyles({
  root: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
})(Container)

const BottomContainer = withStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
})(Container)

const ArrowIcon = styled(Icon)`
  font-size: 60px;
  margin: 0 2px;
`

const AnimationIcon = styled.div`
  -webkit-animation: mover 1s infinite alternate;
  animation: mover 1s infinite alternate;

  @-webkit-keyframes mover {
    0% {
      transform: translateY(0);
    }
    100% {
      transform: translateY(-10px);
    }
  }
  @keyframes mover {
    0% {
      transform: translateY(0);
    }
    100% {
      transform: translateY(-10px);
    }
  }
`

const StyledDownContainer = styled.div<{color: string}>`
  text-align: center;
  margin-bottom: 20px;
  color: ${(props) => props.color};
`

const Top = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  width: 100%;
  flex: 1;
  justify-content: center;
`
